import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { parseRecipientsCsv, downloadCsvTemplate } from "@/lib/csv";
import { autolinkHtml, previewHtml } from "@/lib/bulkEmailHtml";
import { RichTextEditor, isEmptyHtml, sanitizeDocHtml } from "@/components/RichTextEditor";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Send, Users, Loader2, X, Plus, Upload, Download, Save, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const CRIMSON = "#bf3b4f";
const DEFAULT_BODY = "<p>Hi {first_name},</p><p><br></p>";
const keyOf = (r) => r.email.toLowerCase();
const SEND_REMINDER = "Reminder: Bulk Email is for operational messages only. It has no unsubscribe option, so it must not be used for marketing, and should only go to people who are part of Prydes Support and expect to hear from us.";
const PREVIEW_STYLES = "text-sm text-slate-700 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-1 [&_h1]:text-lg [&_h1]:font-semibold [&_h2]:text-base [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-semibold [&_a]:text-teal-700 [&_a]:underline";

// `seed` pre-fills the form (from a template or a past campaign); the parent remounts this on a new seed.
export default function BulkEmailCompose({ senders, seed, onSent }) {
  const [senderId, setSenderId] = useState(seed?.sender_id || "");
  const [replyTo, setReplyTo] = useState(seed?.reply_to || "");
  const [subject, setSubject] = useState(seed?.subject || "");
  const [body, setBody] = useState(seed?.body_html || DEFAULT_BODY);
  const [picked, setPicked] = useState(seed?.recipients || []);
  const [template, setTemplate] = useState(seed?.template || null); // { id, name } when working from a template
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState("");
  const [mEmail, setMEmail] = useState("");
  const [mFirst, setMFirst] = useState("");
  const [sending, setSending] = useState(false);
  const [trackOpens, setTrackOpens] = useState(true);
  const [trackClicks, setTrackClicks] = useState(true);
  const [saveOpen, setSaveOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [savingTpl, setSavingTpl] = useState(false);
  const [csvReport, setCsvReport] = useState(null);
  const editor = useRef(null);
  const fileInput = useRef(null);

  useEffect(() => {
    if (!senderId && senders.length) {
      const d = senders.find((s) => s.is_default) || senders[0];
      setSenderId(d.id);
    }
  }, [senders, senderId]);
  const loadTemplates = () => api.bulkEmailTemplates().then((r) => setTemplates(r.templates || [])).catch(() => {});
  useEffect(() => { loadTemplates(); }, []);

  const addMany = (rows) => {
    const have = new Set(picked.map(keyOf));
    const fresh = rows.filter((r) => r.email && !have.has(keyOf(r)));
    setPicked([...picked, ...fresh]);
    return { added: fresh.length, already: rows.length - fresh.length };
  };

  const addGroup = async (type) => {
    setLoading(type);
    try {
      const res = await api.bulkEmailRecipients({ type });
      const ok = (res.recipients || []).filter((r) => r.valid);
      const missing = (res.recipients || []).length - ok.length;
      addMany(ok.map((r) => ({ email: r.email, first_name: r.first_name, name: r.name, kind: r.kind, ref: r.ref })));
      toast.success(`Added ${ok.length} ${type === "staff" ? "staff" : "participants"}${missing ? ` (${missing} skipped — no valid email)` : ""}.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load recipients."); }
    finally { setLoading(""); }
  };

  const addManual = () => {
    const em = mEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { toast.error("Enter a valid email address."); return; }
    if (!mFirst.trim()) { toast.error("Enter their first name."); return; }
    addMany([{ email: em, first_name: mFirst.trim(), name: mFirst.trim(), kind: "manual" }]);
    setMEmail(""); setMFirst("");
  };

  const onCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("That file is too large (2 MB max)."); return; }
    const parsed = parseRecipientsCsv(await file.text());
    const { added, already } = addMany(parsed.rows);
    setCsvReport({ file: file.name, added, already, noName: parsed.noName, problems: parsed.problems });
    if (added) toast.success(`Added ${added} recipient${added === 1 ? "" : "s"} from ${file.name}.`);
    else toast.error("No recipients could be added from that file.");
  };

  const loadTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    if (!isEmptyHtml(body) && body !== DEFAULT_BODY && !window.confirm("Replace the message you've written with this template?")) return;
    setSubject(t.subject || "");
    setBody(t.body_html);
    if (t.sender_id && senders.some((s) => s.id === t.sender_id)) setSenderId(t.sender_id);
    setReplyTo(t.reply_to || "");
    setTemplate({ id: t.id, name: t.name });
  };

  const tplPayload = (name) => ({ name, subject: subject.trim(), body_html: autolinkHtml(body), sender_id: senderId || null, reply_to: replyTo.trim() || null });
  const saveTemplate = async (mode) => {
    if (isEmptyHtml(body)) { toast.error("Write a message first."); return; }
    setSavingTpl(true);
    try {
      if (mode === "update") {
        await api.bulkEmailUpdateTemplate(template.id, tplPayload(template.name));
        toast.success(`Template “${template.name}” updated.`);
      } else {
        if (!tplName.trim()) { toast.error("Give the template a name."); setSavingTpl(false); return; }
        const r = await api.bulkEmailCreateTemplate(tplPayload(tplName.trim()));
        setTemplate({ id: r.id, name: tplName.trim() });
        toast.success("Template saved.");
      }
      setSaveOpen(false); setTplName(""); loadTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the template."); }
    finally { setSavingTpl(false); }
  };

  const sender = senders.find((s) => s.id === senderId);
  const send = async () => {
    if (!sender) { toast.error("Choose a mailbox to send from."); return; }
    if (!subject.trim()) { toast.error("Add a subject."); return; }
    if (isEmptyHtml(body)) { toast.error("Write a message first."); return; }
    if (!picked.length) { toast.error("Add at least one recipient."); return; }
    if (!window.confirm(`Send to ${picked.length} recipient${picked.length === 1 ? "" : "s"} from ${sender.from_email}?\n\n${SEND_REMINDER}`)) return;
    setSending(true);
    try {
      const res = await api.bulkEmailSend({
        sender_id: senderId, subject: subject.trim(), body_html: autolinkHtml(body),
        reply_to: replyTo.trim() || null, recipients: picked, track_opens: trackOpens, track_clicks: trackClicks,
      });
      toast.success(`Queued ${res.queued} email${res.queued === 1 ? "" : "s"}${res.skipped?.length ? ` (${res.skipped.length} skipped)` : ""}.`);
      onSent();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send."); }
    finally { setSending(false); }
  };

  const preview = useMemo(() => previewHtml(body, picked[0]?.first_name || "Alex", picked[0]?.name || "Alex Smith"), [body, picked]);

  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="md:col-span-2 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-900" role="note" data-testid="bulk-disclaimer">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
        <p>
          <span className="font-semibold">Operational messages only.</span> Do not use Bulk Email for marketing or promotions.
          These emails have no unsubscribe option, so only send to people who are currently part of Prydes Support
          (staff, participants and their contacts) and who expect to receive this kind of information.
        </p>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <Label className="text-xs">Recipients</Label>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => addGroup("staff")} disabled={!!loading} data-testid="bulk-add-staff">
            {loading === "staff" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Users className="h-3.5 w-3.5 mr-1.5" />}All staff
          </Button>
          <Button variant="outline" size="sm" onClick={() => addGroup("participant")} disabled={!!loading} data-testid="bulk-add-participants">
            {loading === "participant" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Users className="h-3.5 w-3.5 mr-1.5" />}All participants
          </Button>
          <Button variant="outline" size="sm" onClick={() => fileInput.current?.click()} data-testid="bulk-upload-csv">
            <Upload className="h-3.5 w-3.5 mr-1.5" />Upload CSV
          </Button>
          <input ref={fileInput} type="file" accept=".csv,text/csv" className="hidden" onChange={onCsv} data-testid="bulk-csv-input" />
          <Button variant="ghost" size="sm" onClick={downloadCsvTemplate} data-testid="bulk-download-template">
            <Download className="h-3.5 w-3.5 mr-1.5" />CSV template
          </Button>
          {!!picked.length && <Button variant="ghost" size="sm" onClick={() => { setPicked([]); setCsvReport(null); }}>Clear all</Button>}
        </div>
        {csvReport && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-[11px] text-slate-600" data-testid="bulk-csv-report">
            <div className="flex items-start justify-between gap-2">
              <div>
                <span className="font-medium text-slate-800">{csvReport.file}</span>: added {csvReport.added}
                {csvReport.already > 0 && `, ${csvReport.already} already in the list`}
                {csvReport.problems.length > 0 && <span className="text-red-600">, {csvReport.problems.length} skipped</span>}.
                {csvReport.noName > 0 && <div className="text-amber-700">{csvReport.noName} had no first name and will be greeted with “there”.</div>}
              </div>
              <button onClick={() => setCsvReport(null)} className="text-slate-400 hover:text-slate-600"><X className="h-3.5 w-3.5" /></button>
            </div>
            {csvReport.problems.length > 0 && (
              <ul className="mt-1.5 max-h-28 overflow-y-auto space-y-0.5 text-red-600">
                {csvReport.problems.slice(0, 50).map((p, i) => <li key={i}>Line {p.line}: {p.reason}{p.email ? ` (${p.email})` : ""}</li>)}
                {csvReport.problems.length > 50 && <li>…and {csvReport.problems.length - 50} more</li>}
              </ul>
            )}
          </div>
        )}
        <div className="flex gap-1.5">
          <Input value={mEmail} onChange={(e) => setMEmail(e.target.value)} className="h-9 text-sm" placeholder="email@example.com" data-testid="bulk-manual-email" />
          <Input value={mFirst} onChange={(e) => setMFirst(e.target.value)} className="h-9 text-sm w-36" placeholder="First name" data-testid="bulk-manual-first"
            onKeyDown={(e) => e.key === "Enter" && addManual()} />
          <Button variant="outline" size="sm" className="h-9" onClick={addManual} data-testid="bulk-manual-add"><Plus className="h-3.5 w-3.5" /></Button>
        </div>
        <div className="text-[11px] text-slate-500">{picked.length} recipient{picked.length === 1 ? "" : "s"}</div>
        <div className="max-h-64 overflow-y-auto flex flex-wrap gap-1.5" data-testid="bulk-picked">
          {picked.map((p) => (
            <span key={keyOf(p)} className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 pl-2 pr-1 py-0.5 text-[11px] text-slate-700" title={p.email}>
              {p.first_name || p.name}
              <button onClick={() => setPicked(picked.filter((x) => keyOf(x) !== keyOf(p)))} className="rounded-full hover:bg-slate-200 p-0.5"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-slate-50 border border-slate-200 px-2.5 py-2" data-testid="bulk-template-bar">
          <FileText className="h-3.5 w-3.5 text-slate-400" />
          <Select value="" onValueChange={loadTemplate}>
            <SelectTrigger className="h-8 w-52 text-xs" data-testid="bulk-template-select">
              <SelectValue placeholder={templates.length ? "Start from a template…" : "No templates yet"} />
            </SelectTrigger>
            <SelectContent>
              {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {template && <span className="text-[11px] text-slate-500">Using “{template.name}”</span>}
          <div className="ml-auto flex gap-1.5">
            {template && (
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => saveTemplate("update")} disabled={savingTpl} data-testid="bulk-template-update">
                Update template
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setSaveOpen(true)} data-testid="bulk-template-save">
              <Save className="h-3.5 w-3.5 mr-1.5" />Save as template
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-xs">Send from</Label>
          <Select value={senderId} onValueChange={setSenderId}>
            <SelectTrigger className="h-9 mt-1" data-testid="bulk-sender"><SelectValue placeholder="Choose a mailbox" /></SelectTrigger>
            <SelectContent>
              {senders.map((s) => <SelectItem key={s.id} value={s.id}>{s.label} — {s.from_email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Reply-to (optional)</Label>
          <Input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} className="h-9 mt-1 text-sm" placeholder="Where replies should go, if not the sender" />
        </div>
        <div>
          <Label className="text-xs">Subject</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9 mt-1 text-sm" data-testid="bulk-subject" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs">Message</Label>
            <div className="flex items-center gap-1 text-[10px] text-slate-400">
              Insert:
              {["{first_name}", "{full_name}"].map((t) => (
                <button key={t} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.current?.insertText(t)}
                  className="rounded border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-slate-600 hover:bg-slate-50" data-testid={`bulk-insert-${t.slice(1, -1)}`}>{t}</button>
              ))}
            </div>
          </div>
          <RichTextEditor ref={editor} value={body} onChange={setBody} headings links testid="bulk-body" placeholder="Write your message…" />
          <div className="text-[10px] text-slate-400 mt-1">Links must be https. Web addresses you type are turned into links automatically.</div>
        </div>
        <label className="flex items-start gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={trackOpens} onChange={(e) => setTrackOpens(e.target.checked)} className="mt-0.5" data-testid="bulk-track-opens" />
          <span>Track opens. Adds a tiny invisible image. Counts are approximate: email apps that block images won't register, and some pre-load images automatically.</span>
        </label>
        <label className="flex items-start gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={trackClicks} onChange={(e) => setTrackClicks(e.target.checked)} className="mt-0.5" data-testid="bulk-track-clicks" />
          <span>Track link clicks. Links are routed through this site first. Some security scanners click links automatically, so counts can run high.</span>
        </label>
        <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5" data-testid="bulk-preview">
          <div className="text-[10px] uppercase text-slate-400 mb-1">Preview{picked[0] ? ` — ${picked[0].first_name || picked[0].name}` : ""}</div>
          <div className={PREVIEW_STYLES} dangerouslySetInnerHTML={{ __html: sanitizeDocHtml(preview) }} />
        </div>
        <Button onClick={send} disabled={sending} style={{ background: CRIMSON }} className="text-white" data-testid="bulk-send">
          {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send to {picked.length || "…"}
        </Button>
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Save as template</DialogTitle></DialogHeader>
          <Label className="text-xs">Template name</Label>
          <Input value={tplName} onChange={(e) => setTplName(e.target.value)} className="h-9 text-sm" placeholder="e.g. Roster update"
            onKeyDown={(e) => e.key === "Enter" && saveTemplate("new")} data-testid="bulk-template-name" />
          <p className="text-[11px] text-slate-500">Saves the subject, message, mailbox and reply-to. Not the recipients.</p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={() => saveTemplate("new")} disabled={savingTpl} style={{ background: CRIMSON }} className="text-white" data-testid="bulk-template-confirm">
              {savingTpl ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
